<script setup lang="ts">
import { personaImages } from '~/models/images'
import type { CarouselI18nItem, CarouselItem } from '~/types/elementsUi'

interface ExperiencesOfOthersSectionProps {
  description?: string
  carouselOnly?: boolean
}

const props = defineProps<ExperiencesOfOthersSectionProps>()

const { t, te, tm, baseKey } = useI18n(
  'components.templates.sections.experiences-of-other-foodies-section'
)

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const computedDescription = computed(() =>
  props.description && te(props.description) ? t(props.description) : props.description
)

const carouselItems = computed<CarouselItem[]>(() => {
  const items: CarouselI18nItem[] = tm('.items')

  return items
    .map<CarouselItem | null>((item) => {
      if (!item.image_DNT) {
        return null
      }
      const image = personaImages[item.image_DNT as keyof typeof personaImages]
      if (!image) {
        return null
      }

      return {
        image: image,
        ...item
      }
    })
    .filter((item) => !!item)
})
</script>

<template>
  <DefineTemplate>
    <VCarousel
      v-slot="{ item }"
      :items="carouselItems"
      :number-of-items="{ mobile: 1, tablet: 2, desktop: 3, widescreen: 4 }"
      loop
      dots
      sr-label=".carousel-aria-label"
    >
      <VCard
        full-height
        class="rounded-xl"
        :ui="{
          content: !item.description && 'justify-normal'
        }"
      >
        <template #image>
          <VImage
            :image="item.image"
            class="m-0! aspect-3/2 h-60 w-full object-cover"
            sizes="xl:25vw lg:33vw md:50vw 100vw"
          />
        </template>

        <template #content>
          <div v-if="item.title || item.subtitle" class="title-subtitle">
            <h3 class="text-h3 font-bold">
              {{ item.title }}
            </h3>
            <p class="italic" v-if="item.subtitle">{{ item.subtitle }}</p>
          </div>

          <p v-if="item.description" class="mt-3 mb-6">
            {{ item.description }}
          </p>

          <q class="font-bold text-pink-700 italic dark:text-success">{{ item.quote }}</q>
        </template>
      </VCard>
    </VCarousel>
  </DefineTemplate>
  <ReuseTemplate v-if="carouselOnly" />
  <VSection v-else :base-key title=".title">
    <template #content>
      <div v-if="computedDescription" class="prose mb-4 max-w-section">
        <p>
          {{ computedDescription }}
        </p>
      </div>

      <ReuseTemplate />
      <div class="section-button mt-0!">
        <VButton :to="{ name: 'registreren-maaltijdzoeker' }" label="buttons.sign-up" />
      </div>
    </template>
  </VSection>
</template>

<style scoped></style>
